import React, { useState, useEffect, useRef } from "react";
import { Result } from "../../types";
import BusinessAuditPDFExport from "./BusinessAuditPDFExport";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { Bar, Doughnut, Radar, Line } from "react-chartjs-2";
import {
  Assessment,
  TrendingUp,
  Warning,
  CheckCircle,
  Assignment,
  Celebration,
  Timeline,
  AttachMoney,
  Speed,
  Business,
  Analytics,
  PriorityHigh,
  Insights,
  Error,
  WorkOff,
  Groups,
  Star,
} from "@mui/icons-material";
import {
  businessAuditRecommendations,
  BusinessAuditRecommendation,
} from "./features";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler
);

interface ResultsPageProps {
  results: Result[];
  onRestart: () => void;
}

const BusinessAuditResults: React.FC<ResultsPageProps> = ({
  results,
  onRestart,
}) => {
  const [animationStep, setAnimationStep] = useState(0);
  const [selectedTab, setSelectedTab] = useState("diagnosis");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimationStep((prev) => (prev < 4 ? prev + 1 : prev));
    }, 500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Analyze quiz responses to generate personalized recommendations
  const generateBusinessDiagnosis = () => {
    const answers = results.map((r) => r.answer.toLowerCase());
    const recommendations: BusinessAuditRecommendation[] = [];
    let riskScore = 0;
    let urgencyLevel = "Low";

    // Performance Analysis (Question 1)
    const performanceAnswer = answers[0];
    if (
      performanceAnswer?.includes("losing") ||
      performanceAnswer?.includes("declining")
    ) {
      recommendations.push(businessAuditRecommendations[0]);
      riskScore += 30;
    } else if (
      performanceAnswer?.includes("stuck") ||
      performanceAnswer?.includes("same level")
    ) {
      recommendations.push(businessAuditRecommendations[0]);
      riskScore += 20;
    } else if (performanceAnswer?.includes("harder than it should")) {
      recommendations.push(businessAuditRecommendations[0]);
      riskScore += 15;
    }

    // Marketing ROI Analysis (Question 2)
    const marketingAnswer = answers[1];
    if (
      marketingAnswer?.includes("not getting results") ||
      marketingAnswer?.includes("can't prove")
    ) {
      recommendations.push(businessAuditRecommendations[1]);
      riskScore += 25;
    } else if (
      marketingAnswer?.includes("hit or miss") ||
      marketingAnswer?.includes("think it's working")
    ) {
      recommendations.push(businessAuditRecommendations[1]);
      riskScore += 15;
    }

    // Brand Perception Analysis (Question 3)
    const brandAnswer = answers[2];
    if (brandAnswer?.includes("no idea") || brandAnswer?.includes("outdated")) {
      recommendations.push(businessAuditRecommendations[2]);
      riskScore += 20;
    } else if (brandAnswer?.includes("could be better")) {
      recommendations.push(businessAuditRecommendations[2]);
      riskScore += 10;
    }

    // Offer Clarity Analysis (Question 4)
    const offerAnswer = answers[3];
    if (
      offerAnswer?.includes("confusing") ||
      offerAnswer?.includes("multiple offers")
    ) {
      recommendations.push(businessAuditRecommendations[3]);
      riskScore += 25;
    } else if (offerAnswer?.includes("needs refining")) {
      recommendations.push(businessAuditRecommendations[3]);
      riskScore += 15;
    }

    // Systems & Processes Analysis (Question 5)
    const systemsAnswer = answers[4];
    if (
      systemsAnswer?.includes("chaotic") ||
      systemsAnswer?.includes("no real systems")
    ) {
      recommendations.push(businessAuditRecommendations[4]);
      riskScore += 30;
    } else if (
      systemsAnswer?.includes("manual") ||
      systemsAnswer?.includes("inefficient")
    ) {
      recommendations.push(businessAuditRecommendations[4]);
      riskScore += 20;
    } else if (systemsAnswer?.includes("bottlenecks")) {
      recommendations.push(businessAuditRecommendations[4]);
      riskScore += 10;
    }

    // Team Alignment Analysis (Question 6)
    const teamAnswer = answers[5];
    if (teamAnswer?.includes("confusion") || teamAnswer?.includes("just me")) {
      recommendations.push(businessAuditRecommendations[5]);
      riskScore += 20;
    } else if (teamAnswer?.includes("not tracked")) {
      recommendations.push(businessAuditRecommendations[5]);
      riskScore += 10;
    }

    // Customer Experience Analysis (Question 7)
    const customerAnswer = answers[6];
    if (
      customerAnswer?.includes("complain") ||
      customerAnswer?.includes("never measured")
    ) {
      recommendations.push(businessAuditRecommendations[6]);
      riskScore += 25;
    } else if (customerAnswer?.includes("decent reviews")) {
      recommendations.push(businessAuditRecommendations[6]);
      riskScore += 10;
    }

    // Performance Tracking Analysis (Question 8)
    const trackingAnswer = answers[7];
    if (
      trackingAnswer?.includes("never") ||
      trackingAnswer?.includes("gut feeling")
    ) {
      recommendations.push(businessAuditRecommendations[7]);
      riskScore += 25;
    } else if (trackingAnswer?.includes("occasionally")) {
      recommendations.push(businessAuditRecommendations[7]);
      riskScore += 15;
    }

    // Problem Response Analysis (Question 9)
    const problemAnswer = answers[8];
    if (
      problemAnswer?.includes("ignore") ||
      problemAnswer?.includes("not sure")
    ) {
      recommendations.push(businessAuditRecommendations[8]);
      riskScore += 25;
    } else if (problemAnswer?.includes("patch")) {
      recommendations.push(businessAuditRecommendations[8]);
      riskScore += 15;
    }

    // Business Owner Frustration Analysis (Question 10)
    const frustrationAnswer = answers[9];
    if (
      frustrationAnswer?.includes("stuck") ||
      frustrationAnswer?.includes("don't know what to fix")
    ) {
      recommendations.push(businessAuditRecommendations[9]);
      riskScore += 30;
    } else if (
      frustrationAnswer?.includes("working hard") ||
      frustrationAnswer?.includes("aren't converting")
    ) {
      recommendations.push(businessAuditRecommendations[9]);
      riskScore += 20;
    }

    // Determine urgency level
    if (riskScore >= 60) {
      urgencyLevel = "Critical";
    } else if (riskScore >= 40) {
      urgencyLevel = "High";
    } else if (riskScore >= 20) {
      urgencyLevel = "Medium";
    }

    // Remove duplicates and ensure we have relevant recommendations
    const uniqueRecommendations = Array.from(
      new Set(recommendations.map((r) => r.id))
    )
      .map((id) => recommendations.find((r) => r.id === id)!)
      .filter(Boolean);

    // If no specific recommendations, add general business assessment
    if (uniqueRecommendations.length === 0) {
      uniqueRecommendations.push(
        businessAuditRecommendations[0],
        businessAuditRecommendations[1]
      );
    }

    return {
      recommendations: uniqueRecommendations,
      riskScore,
      urgencyLevel,
      totalIssues: uniqueRecommendations.length,
      criticalIssues: uniqueRecommendations.filter(
        (r) => r.priority === "Critical"
      ).length,
      highIssues: uniqueRecommendations.filter((r) => r.priority === "High")
        .length,
    };
  };

  const diagnosis = generateBusinessDiagnosis();
  const {
    recommendations,
    riskScore,
    urgencyLevel,
    totalIssues,
    criticalIssues,
    highIssues,
  } = diagnosis;

  // Chart data for business audit analysis
  const riskAssessmentData = {
    labels: ["Current Performance", "Market Average", "High Performers"],
    datasets: [
      {
        label: "Business Health Score",
        data: [100 - riskScore, 75, 90],
        backgroundColor: [
          riskScore >= 60 ? "#ef4444" : riskScore >= 40 ? "#f59e0b" : "#10b981",
          "#6b7280",
          "#10b981",
        ],
        borderColor: [
          riskScore >= 60 ? "#dc2626" : riskScore >= 40 ? "#d97706" : "#059669",
          "#4b5563",
          "#059669",
        ],
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const issueBreakdownData = {
    labels: [
      "Critical Issues",
      "High Priority",
      "Medium Priority",
      "Opportunities",
    ],
    datasets: [
      {
        data: [
          criticalIssues,
          highIssues,
          recommendations.filter((r) => r.priority === "Medium").length,
          Math.max(0, 3 - totalIssues),
        ],
        backgroundColor: ["#ef4444", "#f59e0b", "#3b82f6", "#10b981"],
        borderWidth: 0,
        hoverOffset: 10,
      },
    ],
  };

  const frameworkCoverageData = {
    labels: [
      "Strategy",
      "Marketing",
      "Operations",
      "Customer Experience",
      "Team",
      "Analytics",
      "Systems",
      "Growth",
    ],
    datasets: [
      {
        label: "Areas Identified for Improvement",
        data: [
          recommendations.some((r) => r.framework.includes("Strategic"))
            ? 85
            : 20,
          recommendations.some((r) => r.framework.includes("Marketing"))
            ? 90
            : 25,
          recommendations.some((r) => r.framework.includes("Operations"))
            ? 80
            : 30,
          recommendations.some((r) => r.framework.includes("Customer"))
            ? 85
            : 35,
          recommendations.some((r) => r.framework.includes("Team")) ? 75 : 40,
          recommendations.some((r) => r.framework.includes("Analytics"))
            ? 70
            : 45,
          recommendations.some((r) => r.framework.includes("Operations"))
            ? 80
            : 25,
          recommendations.some((r) => r.framework.includes("Performance"))
            ? 85
            : 30,
        ],
        backgroundColor: "rgba(201, 167, 80, 0.3)",
        borderColor: "#c9a750ff",
        pointBackgroundColor: "#c9a750ff",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#c9a750ff",
      },
    ],
  };

  const auditImpactData = {
    labels: recommendations
      .slice(0, 5)
      .map((r) => r.title.split(" ").slice(0, 2).join(" ")),
    datasets: [
      {
        label: "Impact Score (0-100)",
        data: [95, 90, 85, 80, 75],
        backgroundColor: "rgba(16, 185, 129, 0.8)",
        borderColor: "#10b981",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
      {
        label: "Implementation Ease",
        data: [85, 80, 90, 75, 85],
        backgroundColor: "rgba(59, 130, 246, 0.6)",
        borderColor: "#3b82f6",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  return (
    <div
      ref={resultsRef}
      style={{
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        minHeight: "100vh",
        padding: isMobile ? "1rem" : "2rem",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Enhanced Header */}
        <div
          className={animationStep >= 0 ? "fade-in" : ""}
          style={{
            textAlign: "center",
            marginBottom: isMobile ? "2rem" : "3rem",
            color: "white",
          }}
        >
          <div
            style={{
              fontSize: isMobile ? "2.5rem" : "4rem",
              marginBottom: "1rem",
            }}
          >
            <Assessment
              sx={{ fontSize: isMobile ? "2.5rem" : "4rem", color: "white" }}
            />
          </div>
          <h1
            style={{
              fontFamily: "var(--font-headline)",
              fontSize: isMobile
                ? "var(--font-size-headline-mobile)"
                : "var(--font-size-headline)",
              fontWeight: "800",
              marginBottom: "1rem",
              background:
                "linear-gradient(35deg,rgb(215, 227, 255) 0%,rgb(155, 188, 255) 30%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              lineHeight: "1.2",
              padding: isMobile ? "0 1rem" : "0",
            }}
          >
            Your Business Health Diagnosis
          </h1>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: isMobile
                ? "var(--font-size-body-mobile)"
                : "var(--font-size-body)",
              opacity: 0.9,
              maxWidth: "700px",
              margin: "0 auto 2rem",
              lineHeight: "1.6",
              padding: isMobile ? "0 1rem" : "0",
            }}
          >
            Based on your responses, we've identified key areas that may be
            limiting your business growth. Our FREE 3-Day Business Audit can
            provide detailed insights and solutions.
          </p>

          {/* Key Statistics */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)",
              gap: isMobile ? "1rem" : "1.5rem",
              maxWidth: "900px",
              margin: "0 auto",
              padding: isMobile ? "0 1rem" : "0",
            }}
          >
            <div
              style={{
                backgroundColor:
                  riskScore >= 70
                    ? "rgba(34, 197, 94, 0.2)"
                    : riskScore >= 40
                    ? "rgba(245, 158, 11, 0.2)"
                    : "rgba(239, 68, 68, 0.2)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
                border:
                  riskScore >= 70
                    ? "1px solid rgba(34, 197, 94, 0.3)"
                    : riskScore >= 40
                    ? "1px solid rgba(245, 158, 11, 0.3)"
                    : "1px solid rgba(239, 68, 68, 0.3)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                minHeight: isMobile ? "120px" : "140px",
                justifyContent: "center",
              }}
            >
              <TrendingUp
                sx={{
                  fontSize: isMobile ? "2rem" : "2.5rem",
                  marginBottom: "0.5rem",
                  color:
                    riskScore >= 70
                      ? "#22c55e"
                      : riskScore >= 40
                      ? "#f59e0b"
                      : "#ef4444",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.5rem" : "1.8rem",
                  fontWeight: "bold",
                  marginBottom: "0.25rem",
                  color:
                    riskScore >= 70
                      ? "#22c55e"
                      : riskScore >= 40
                      ? "#f59e0b"
                      : "#ef4444",
                }}
              >
                {riskScore}%
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  color: "rgba(255, 255, 255, 0.9)",
                  fontWeight: "500",
                }}
              >
                Business Health Score
              </div>
            </div>
            <div
              style={{
                backgroundColor:
                  totalIssues >= 7
                    ? "rgba(239, 68, 68, 0.2)"
                    : totalIssues >= 4
                    ? "rgba(245, 158, 11, 0.2)"
                    : "rgba(34, 197, 94, 0.2)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
                border:
                  totalIssues >= 7
                    ? "1px solid rgba(239, 68, 68, 0.3)"
                    : totalIssues >= 4
                    ? "1px solid rgba(245, 158, 11, 0.3)"
                    : "1px solid rgba(34, 197, 94, 0.3)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                minHeight: isMobile ? "120px" : "140px",
                justifyContent: "center",
              }}
            >
              <Warning
                sx={{
                  fontSize: isMobile ? "2rem" : "2.5rem",
                  marginBottom: "0.5rem",
                  color:
                    totalIssues >= 7
                      ? "#ef4444"
                      : totalIssues >= 4
                      ? "#f59e0b"
                      : "#22c55e",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.5rem" : "1.8rem",
                  fontWeight: "bold",
                  marginBottom: "0.25rem",
                  color:
                    totalIssues >= 7
                      ? "#ef4444"
                      : totalIssues >= 4
                      ? "#f59e0b"
                      : "#22c55e",
                }}
              >
                {totalIssues}
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  color: "rgba(255, 255, 255, 0.9)",
                  fontWeight: "500",
                }}
              >
                Areas for Improvement
              </div>
            </div>
            <div
              style={{
                backgroundColor: urgencyLevel.includes("Critical")
                  ? "rgba(239, 68, 68, 0.2)"
                  : urgencyLevel.includes("High")
                  ? "rgba(245, 158, 11, 0.2)"
                  : urgencyLevel.includes("Medium")
                  ? "rgba(59, 130, 246, 0.2)"
                  : "rgba(34, 197, 94, 0.2)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
                border: urgencyLevel.includes("Critical")
                  ? "1px solid rgba(239, 68, 68, 0.3)"
                  : urgencyLevel.includes("High")
                  ? "1px solid rgba(245, 158, 11, 0.3)"
                  : urgencyLevel.includes("Medium")
                  ? "1px solid rgba(59, 130, 246, 0.3)"
                  : "1px solid rgba(34, 197, 94, 0.3)",
                gridColumn: isMobile ? "1 / -1" : "3 / 4",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                minHeight: isMobile ? "120px" : "140px",
                justifyContent: "center",
              }}
            >
              <PriorityHigh
                sx={{
                  fontSize: isMobile ? "2rem" : "2.5rem",
                  marginBottom: "0.5rem",
                  color: urgencyLevel.includes("Critical")
                    ? "#ef4444"
                    : urgencyLevel.includes("High")
                    ? "#f59e0b"
                    : urgencyLevel.includes("Medium")
                    ? "#3b82f6"
                    : "#22c55e",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.1rem" : "1.3rem",
                  fontWeight: "bold",
                  marginBottom: "0.25rem",
                  color: urgencyLevel.includes("Critical")
                    ? "#ef4444"
                    : urgencyLevel.includes("High")
                    ? "#f59e0b"
                    : urgencyLevel.includes("Medium")
                    ? "#3b82f6"
                    : "#22c55e",
                  lineHeight: "1.2",
                }}
              >
                {urgencyLevel}
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  color: "rgba(255, 255, 255, 0.9)",
                  fontWeight: "500",
                }}
              >
                Priority Level
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div
          className={animationStep >= 1 ? "fade-in" : ""}
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: isMobile ? "2rem" : "3rem",
            opacity: animationStep >= 1 ? 1 : 0,
            transform:
              animationStep >= 1 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-in-out",
            flexWrap: "wrap",
            gap: isMobile ? "0.5rem" : "0",
          }}
        >
          {["diagnosis", "analytics", "audit-benefits", "your-responses"].map(
            (tab) => (
              <button
                key={tab}
                data-tab={tab}
                onClick={() => setSelectedTab(tab)}
                style={{
                  padding: isMobile ? "0.75rem 1rem" : "1rem 2rem",
                  margin: isMobile ? "0" : "0 0.5rem",
                  backgroundColor:
                    selectedTab === tab
                      ? "rgba(255, 255, 255, 0.9)"
                      : "rgba(255, 255, 255, 0.1)",
                  color: selectedTab === tab ? "#1f2937" : "white",
                  border: "none",
                  borderRadius: "12px",
                  cursor: "pointer",
                  fontSize: isMobile ? "0.9rem" : "1.1rem",
                  fontWeight: "600",
                  transition: "all 0.3s ease",
                  backdropFilter: "blur(10px)",
                  textTransform: "capitalize",
                  minWidth: isMobile ? "auto" : "120px",
                }}
              >
                {tab === "audit-benefits"
                  ? "Why Audit?"
                  : tab === "your-responses"
                  ? "Your Responses"
                  : tab.replace("-", " ")}
              </button>
            )
          )}
        </div>

        {/* Diagnosis Tab */}
        {selectedTab === "diagnosis" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Business Issues Identified */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : recommendations.length === 1
                  ? "1fr"
                  : recommendations.length === 2
                  ? "1fr 1fr"
                  : "repeat(auto-fit, minmax(350px, 1fr))",
                gap: isMobile ? "1.5rem" : "2rem",
                marginBottom: isMobile ? "2rem" : "3rem",
                justifyItems: "stretch",
                alignItems: "stretch",
                gridAutoRows: "1fr",
              }}
            >
              {recommendations.map((recommendation, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: "20px",
                    padding: isMobile ? "1.5rem" : "2rem",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    transform: `translateY(0px)`,
                    animation: `slideUp 0.6s ease-out ${index * 0.1}s both`,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: isMobile ? "auto" : "320px",
                    height: "100%",
                  }}
                >
                  {/* Header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: isMobile ? "flex-start" : "center",
                      marginBottom: "1.5rem",
                      flexDirection: isMobile ? "column" : "row",
                      textAlign: isMobile ? "center" : "left",
                    }}
                  >
                    <div
                      style={{
                        fontSize: isMobile ? "2rem" : "3rem",
                        marginRight: isMobile ? "0" : "1rem",
                        marginBottom: isMobile ? "1rem" : "0",
                        color: "#c9a750ff",
                      }}
                    >
                      <Warning sx={{ fontSize: isMobile ? "2rem" : "3rem" }} />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#c9a750ff",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        {recommendation.category}
                      </div>
                      <h3
                        style={{
                          color: "#1f2937",
                          margin: 0,
                          fontSize: isMobile ? "1.2rem" : "1.4rem",
                          lineHeight: "1.3",
                        }}
                      >
                        {recommendation.title}
                      </h3>
                    </div>
                  </div>

                  {/* Description */}
                  <p
                    style={{
                      color: "#6b7280",
                      lineHeight: "1.6",
                      marginBottom: "1.5rem",
                      fontSize: isMobile ? "0.9rem" : "1rem",
                    }}
                  >
                    {recommendation.description}
                  </p>

                  {/* Symptoms */}
                  <div style={{ marginBottom: "1.5rem" }}>
                    <h4
                      style={{
                        color: "#1f2937",
                        marginBottom: "0.75rem",
                        fontSize: "1rem",
                      }}
                    >
                      Symptoms to Watch For:
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: "0.5rem",
                      }}
                    >
                      {recommendation.symptoms
                        .slice(0, 3)
                        .map((symptom, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              fontSize: isMobile ? "0.8rem" : "0.9rem",
                              color: "#4b5563",
                            }}
                          >
                            <span
                              style={{
                                color: "#ef4444",
                                marginRight: "0.5rem",
                              }}
                            >
                              ⚠
                            </span>
                            {symptom}
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Impact & Priority */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: "10px",
                      marginTop: "10px",
                      padding: "1rem",
                      backgroundColor: "rgba(201, 167, 80, 0.1)",
                      borderRadius: "12px",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Priority Level
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color:
                            recommendation.priority === "Critical"
                              ? "#ef4444"
                              : recommendation.priority === "High"
                              ? "#f59e0b"
                              : "#3b82f6",
                        }}
                      >
                        {recommendation.priority}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {selectedTab === "analytics" && (
          <div
            className={
              animationStep >= 2
                ? "fade-in analytics-container"
                : "analytics-container"
            }
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Charts Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                gap: isMobile ? "1.5rem" : "2rem",
                marginBottom: isMobile ? "2rem" : "3rem",
              }}
            >
              {/* Business Health Score */}
              <div
                data-chart="business-health"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <TrendingUp
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Business Health Assessment
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Your current business health compared to market standards
                </p>
                <Bar
                  data={riskAssessmentData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            return `Health Score: ${context.parsed.y}%`;
                          },
                        },
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 100,
                        title: {
                          display: !isMobile,
                          text: "Health Score (%)",
                        },
                        ticks: {
                          font: {
                            size: isMobile ? 10 : 12,
                          },
                        },
                      },
                      x: {
                        ticks: {
                          font: {
                            size: isMobile ? 8 : 10,
                          },
                        },
                      },
                    },
                  }}
                />
              </div>

              {/* Issue Breakdown */}
              <div
                data-chart="issue-breakdown"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Warning
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Issue Priority Breakdown
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Distribution of business issues by priority level
                </p>
                <Doughnut
                  data={issueBreakdownData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: "bottom" as const,
                        labels: {
                          usePointStyle: true,
                          padding: isMobile ? 10 : 20,
                          font: {
                            size: isMobile ? 10 : 12,
                          },
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            return `${context.label}: ${context.parsed} issues`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>

              {/* IN.8 Framework Coverage */}
              <div
                data-chart="framework-coverage"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Assessment
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  IN.8 Framework™ Analysis
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Business dimensions requiring attention in our comprehensive
                  framework
                </p>
                <Radar
                  data={frameworkCoverageData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    scales: {
                      r: {
                        beginAtZero: true,
                        max: 100,
                        ticks: {
                          font: {
                            size: isMobile ? 8 : 10,
                          },
                        },
                        pointLabels: {
                          font: {
                            size: isMobile ? 8 : 10,
                          },
                        },
                      },
                    },
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                  }}
                />
              </div>

              {/* Audit Impact Potential */}
              <div
                data-chart="audit-impact"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Insights
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Audit Impact Potential
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Expected impact and implementation ease for top
                  recommendations
                </p>
                <Line
                  data={auditImpactData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: "bottom" as const,
                        labels: {
                          usePointStyle: true,
                          padding: isMobile ? 10 : 20,
                          font: {
                            size: isMobile ? 10 : 12,
                          },
                        },
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 100,
                        title: {
                          display: !isMobile,
                          text: "Score (0-100)",
                        },
                        ticks: {
                          font: {
                            size: isMobile ? 10 : 12,
                          },
                        },
                      },
                      x: {
                        ticks: {
                          font: {
                            size: isMobile ? 8 : 10,
                          },
                          maxRotation: isMobile ? 45 : 0,
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Audit Benefits Tab */}
        {selectedTab === "audit-benefits" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Why You Need Our FREE Business Audit */}
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "20px",
                padding: isMobile ? "2rem" : "3rem",
                marginBottom: "2rem",
                backdropFilter: "blur(10px)",
                textAlign: "center",
              }}
            >
              <h2
                style={{
                  color: "#1f2937",
                  marginBottom: "1.5rem",
                  fontSize: isMobile ? "1.8rem" : "2.5rem",
                  fontWeight: "700",
                }}
              >
                Why Your Business Needs Our FREE 3-Day Audit
              </h2>
              <p
                style={{
                  color: "#6b7280",
                  fontSize: isMobile ? "1rem" : "1.2rem",
                  lineHeight: "1.6",
                  maxWidth: "800px",
                  margin: "0 auto 2rem",
                }}
              >
                Based on your assessment, your business shows several symptoms
                that our IN.8 Framework™ can address. Here's how our
                comprehensive audit will help:
              </p>

              {/* Audit Benefits Grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                  gap: "2rem",
                  marginTop: "2rem",
                }}
              >
                <div
                  style={{
                    padding: "1.5rem",
                    backgroundColor: "rgba(16, 185, 129, 0.1)",
                    borderRadius: "12px",
                    borderLeft: "4px solid #10b981",
                  }}
                >
                  <CheckCircle
                    sx={{
                      fontSize: "2rem",
                      color: "#10b981",
                      marginBottom: "1rem",
                    }}
                  />
                  <h3 style={{ color: "#1f2937", marginBottom: "1rem" }}>
                    Complete Clarity
                  </h3>
                  <p style={{ color: "#6b7280", lineHeight: "1.6" }}>
                    Know exactly what's holding you back across all 8 business
                    dimensions. No more guessing or treating symptoms.
                  </p>
                </div>

                <div
                  style={{
                    padding: "1.5rem",
                    backgroundColor: "rgba(59, 130, 246, 0.1)",
                    borderRadius: "12px",
                    borderLeft: "4px solid #3b82f6",
                  }}
                >
                  <Timeline
                    sx={{
                      fontSize: "2rem",
                      color: "#3b82f6",
                      marginBottom: "1rem",
                    }}
                  />
                  <h3 style={{ color: "#1f2937", marginBottom: "1rem" }}>
                    Prioritized Roadmap
                  </h3>
                  <p style={{ color: "#6b7280", lineHeight: "1.6" }}>
                    See which fixes will create the biggest impact first. Get a
                    clear 30-day action plan with measurable outcomes.
                  </p>
                </div>

                <div
                  style={{
                    padding: "1.5rem",
                    backgroundColor: "rgba(245, 158, 11, 0.1)",
                    borderRadius: "12px",
                    borderLeft: "4px solid #f59e0b",
                  }}
                >
                  <Speed
                    sx={{
                      fontSize: "2rem",
                      color: "#f59e0b",
                      marginBottom: "1rem",
                    }}
                  />
                  <h3 style={{ color: "#1f2937", marginBottom: "1rem" }}>
                    Quick Wins
                  </h3>
                  <p style={{ color: "#6b7280", lineHeight: "1.6" }}>
                    Identify changes you can implement immediately for results.
                    Start seeing improvements within 30 days.
                  </p>
                </div>

                <div
                  style={{
                    padding: "1.5rem",
                    backgroundColor: "rgba(168, 85, 247, 0.1)",
                    borderRadius: "12px",
                    borderLeft: "4px solid #a855f7",
                  }}
                >
                  <Analytics
                    sx={{
                      fontSize: "2rem",
                      color: "#a855f7",
                      marginBottom: "1rem",
                    }}
                  />
                  <h3 style={{ color: "#1f2937", marginBottom: "1rem" }}>
                    Objective Assessment
                  </h3>
                  <p style={{ color: "#6b7280", lineHeight: "1.6" }}>
                    Get expert external perspective on your blind spots.
                    Unbiased analysis with 24+ years of business transformation
                    experience.
                  </p>
                </div>
              </div>

              {/* Value Proposition */}
              <div
                style={{
                  marginTop: "3rem",
                  padding: "2rem",
                  backgroundColor: "rgba(201, 167, 80, 0.1)",
                  borderRadius: "12px",
                  border: "2px solid #c9a750ff",
                }}
              >
                <h3
                  style={{
                    color: "#1f2937",
                    marginBottom: "1rem",
                    fontSize: isMobile ? "1.5rem" : "2rem",
                  }}
                >
                  What You'll Receive (Worth $2,500)
                </h3>
                <ul
                  style={{
                    color: "#4b5563",
                    textAlign: "left",
                    lineHeight: "1.8",
                    fontSize: isMobile ? "1rem" : "1.1rem",
                    maxWidth: "600px",
                    margin: "0 auto",
                  }}
                >
                  <li>Personalized IN.8 Snapshot™ report (10-15 pages)</li>
                  <li>Detailed findings from 3 analyzed business dimensions</li>
                  <li>Growth gap identification across all 8 dimensions</li>
                  <li>Top 3-5 priority recommendations</li>
                  <li>30-day implementation roadmap</li>
                  <li>Expected ROI metrics for each recommendation</li>
                  <li>Strategic consultation session</li>
                </ul>
              </div>

              {/* CTA */}
              <div style={{ marginTop: "2rem" }}>
                <a
                  href="https://intheraconsultinggroup.com/v2/preview/kog0zzskGk6SSglPdLXw"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block",
                    padding: "1rem 2rem",
                    backgroundColor: "#10b981",
                    color: "white",
                    textDecoration: "none",
                    borderRadius: "12px",
                    fontSize: "1.2rem",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(16, 185, 129, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(16, 185, 129, 0.3)";
                  }}
                >
                  🚀 Book Your FREE 3-Day Business Audit
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Your Responses Tab */}
        {selectedTab === "your-responses" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: "1rem",
                marginBottom: "2rem",
              }}
            >
              {results.map((result, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: "16px",
                    padding: isMobile ? "1.5rem" : "2rem",
                    backdropFilter: "blur(10px)",
                    borderLeft: "4px solid #c9a750ff",
                  }}
                >
                  <h3
                    style={{
                      color: "#1f2937",
                      marginBottom: "1rem",
                      fontSize: isMobile ? "1.1rem" : "1.3rem",
                      fontWeight: "600",
                    }}
                  >
                    Q{index + 1}: {result.question}
                  </h3>
                  <p
                    style={{
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1rem" : "1.1rem",
                      fontWeight: "500",
                      margin: 0,
                    }}
                  >
                    Your Answer: {result.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div
          className={animationStep >= 3 ? "fade-in" : ""}
          style={{
            display: "flex",
            justifyContent: "center",
            gap: isMobile ? "1rem" : "2rem",
            marginTop: isMobile ? "2rem" : "3rem",
            opacity: animationStep >= 3 ? 1 : 0,
            transform:
              animationStep >= 3 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-in-out",
            flexWrap: "wrap",
            padding: isMobile ? "0 1rem" : "0",
          }}
        >
          <BusinessAuditPDFExport
            results={results}
            recommendations={recommendations}
            resultsRef={resultsRef}
          />
          <button
            onClick={onRestart}
            style={{
              padding: isMobile ? "0.75rem 1.5rem" : "1rem 2rem",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "white",
              border: "2px solid #c9a750ff",
              borderRadius: "12px",
              cursor: "pointer",
              fontSize: isMobile ? "1rem" : "1.1rem",
              fontWeight: "600",
              transition: "all 0.3s ease",
              backdropFilter: "blur(10px)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#c9a750ff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.2)";
            }}
          >
            Retake Assessment
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessAuditResults;
